import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  LayoutDashboard, Briefcase, BookOpen, FolderOpen, FileText, 
  Users, Mail, ClipboardList, GraduationCap, Menu, X, LogOut, ChevronDown, 
  UserCheck, MapPin, CalendarPlus, FileStack, CalendarDays, Calendar, CheckCircle,
  UserSearch, Workflow, Database, Globe, Building, Handshake, FileEdit, Lightbulb
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c6a1162-6fec-4154-8fe3-0b918a141fc6/artifacts/ojlspj1s_Untitled%20design.png';

export const AdminLayout = () => {
  const { t, direction } = useLanguage();
  const { user, logout, isAdmin, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [atsExpanded, setAtsExpanded] = useState(true);
  const [trainingExpanded, setTrainingExpanded] = useState(true);
  const [cmsExpanded, setCmsExpanded] = useState(true);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/login');
    }
  }, [loading, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const navItems = [
    { icon: LayoutDashboard, label: t('admin.overview'), href: '/admin' },
    // ATS Section
    { type: 'section', label: 'ATS / Recruitment', icon: UserSearch, key: 'ats' },
    { icon: Database, label: 'Candidates', href: '/admin/candidates', section: 'ats' },
    { icon: Briefcase, label: 'ATS Jobs', href: '/admin/ats-jobs', section: 'ats' },
    { icon: Workflow, label: 'Applications Pipeline', href: '/admin/applications', section: 'ats' },
    { icon: ClipboardList, label: 'Recruitment Requests', href: '/admin/recruitment-requests', section: 'ats' },
    // Training Section
    { type: 'section', label: 'Training Operations', icon: GraduationCap, key: 'training' },
    { icon: FileStack, label: 'Course Templates', href: '/admin/course-templates', section: 'training' },
    { icon: CalendarDays, label: 'Course Sessions', href: '/admin/course-sessions', section: 'training' },
    { icon: CalendarPlus, label: 'Bulk Schedule', href: '/admin/bulk-schedule', section: 'training' },
    { icon: Calendar, label: 'Yearly Roll-Forward', href: '/admin/yearly-schedule', section: 'training' },
    { icon: GraduationCap, label: 'Enrollment Requests', href: '/admin/enrollment-requests', section: 'training' },
    { icon: CheckCircle, label: 'Confirmed Enrollments', href: '/admin/confirmed-enrollments', section: 'training' },
    { icon: GraduationCap, label: 'Training Requests', href: '/admin/training-requests', section: 'training' },
    { icon: CalendarPlus, label: 'Schedule Requests', href: '/admin/schedule-requests', section: 'training' },
    // Website Content Section
    { type: 'section', label: 'Website Content', icon: Globe, key: 'cms' },
    { icon: FileEdit, label: 'Services', href: '/admin/cms/services', section: 'cms' },
    { icon: Building, label: 'Clients', href: '/admin/cms/clients', section: 'cms' },
    { icon: Handshake, label: 'Partners', href: '/admin/cms/partners', section: 'cms' },
    { icon: FileText, label: 'Page Content', href: '/admin/cms/page-content', section: 'cms' },
    { icon: Lightbulb, label: 'WeDo Insights', href: '/admin/insights', section: 'cms' },
    // Config Section
    { type: 'divider' },
    { icon: FolderOpen, label: 'Categories', href: '/admin/categories' },
    { icon: MapPin, label: 'Training Cities', href: '/admin/training-cities' },
    { icon: Mail, label: 'Inquiries', href: '/admin/inquiries' },
    { icon: Users, label: 'Users', href: '/admin/users' },
  ];

  const isActive = (href) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  const toggleSection = (key) => {
    if (key === 'ats') setAtsExpanded(!atsExpanded);
    if (key === 'training') setTrainingExpanded(!trainingExpanded);
    if (key === 'cms') setCmsExpanded(!cmsExpanded);
  };

  const isSectionExpanded = (key) => {
    if (key === 'ats') return atsExpanded;
    if (key === 'training') return trainingExpanded;
    if (key === 'cms') return cmsExpanded;
    return true;
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 start-0 end-0 h-16 bg-white border-b border-[#E5E7EB] z-50 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center">
          <img src={LOGO_URL} alt="WeDo" className="h-8 w-auto" />
        </Link>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 start-0 h-full w-64 bg-white border-e border-[#E5E7EB] z-40 transform transition-transform duration-200
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : (direction === 'rtl' ? 'translate-x-full' : '-translate-x-full')}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-[#E5E7EB]">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="WeDo" className="h-8 w-auto" />
              <span className="font-semibold text-[#111827]">Admin</span>
            </Link>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            {navItems.map((item, index) => {
              // Divider
              if (item.type === 'divider') {
                return <div key={index} className="h-px bg-[#E5E7EB] my-3 mx-4" />;
              }
              
              // Section header
              if (item.type === 'section') {
                return (
                  <button
                    key={index}
                    onClick={() => toggleSection(item.key)}
                    className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-[#6B7280] uppercase tracking-wider hover:bg-[#F9FAFB]"
                  >
                    <span className="flex items-center gap-2">
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isSectionExpanded(item.key) ? '' : '-rotate-90'}`} />
                  </button>
                );
              }
              
              // Section item (collapsible)
              if (item.section && !isSectionExpanded(item.section)) {
                return null;
              }
              
              return (
                <Link
                  key={index}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-6 py-2.5 text-sm transition-colors
                    ${item.section ? 'ps-8' : ''}
                    ${isActive(item.href) 
                      ? 'bg-[#33AADD]/10 text-[#33AADD] border-e-2 border-[#33AADD]' 
                      : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111827]'}
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="border-t border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3 mb-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-[#33AADD] flex items-center justify-center text-white font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#111827] truncate">{user?.name}</p>
                <p className="text-xs text-[#6B7280] truncate">{user?.role}</p>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-[#6B7280]" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 me-2" />
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ms-64 pt-16 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
